import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  X,
  GripVertical,
  Pencil,
  Trash2,
  Check,
  DollarSign,
  User,
  Calendar,
  Loader2,
  FileText,
  Briefcase,
  AlertTriangle,
  Lock,
  Settings2,
  Tag as TagIcon,
  Inbox,
  Pencil as PencilIcon,
  Mail,
  Phone,
  Ticket,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { Button } from "../components/ui/button";
import { pipelineApi, estimatesApi, jobsApi, customersApi, usersApi } from "../lib/api";
import { useAuth, formatApiErrorDetail } from "../contexts/AuthContext";
import CustomCardModal from "../components/pipeline/CustomCardModal";
import NewPipelineModal from "../components/pipeline/NewPipelineModal";
import CustomerProfileOverlay from "../components/CustomerProfileOverlay";
import PlatformLeadDrawer from "../components/platform/PlatformLeadDrawer";
import JobsBoardView from "../components/pipeline/JobsBoardView";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const SALES_STATUSES = ["Draft", "Sent", "Accepted", "Converted", "Declined", "Expired"];
const JOB_STATUSES = ["Unassigned", "Scheduled", "Dispatched", "En Route", "On Site", "In Progress", "Completed", "Canceled"];

const PRIORITY_COLOR = {
  High: "bg-rose-50 text-rose-700 border-rose-200",
  Medium: "bg-amber-50 text-amber-700 border-amber-200",
  Low: "bg-slate-50 text-slate-600 border-slate-200",
};

const isSystemBoard = (b) => b === "sales" || b === "jobs";

// System events a custom stage's trigger can subscribe to (triggerType === "event").
// job/estimate status changes are handled via the "Map to existing status" mode instead,
// which covers every built-in Sales/Jobs status without needing a dedicated event per status.
// Tenant pipelines only ever see the tenant list; Platform Console pipelines (embedded)
// only ever see the platform list — the two never mix (mirrors backend PLATFORM_TRIGGER_EVENTS).
const TENANT_TRIGGER_EVENT_OPTIONS = [
  { value: "estimate_sent", label: "Estimate is sent" },
  { value: "estimate_signed", label: "Estimate is signed" },
  { value: "payment_received", label: "Payment is received" },
  { value: "invoice_created", label: "Invoice is created" },
  { value: "customer_created", label: "New customer is created" },
  { value: "technician_dispatched", label: "Technician is dispatched" },
  { value: "job_closed", label: "Job is closed" },
  { value: "new_lead_captured", label: "New lead captured (contact form)" },
  { value: "job.scheduled", label: "Job is scheduled" },
  { value: "job.enroute", label: "Technician is en route" },
  { value: "job.onsite", label: "Technician is on site" },
  { value: "job.work_started", label: "Work has started on the job" },
  { value: "job.completed", label: "Job is completed" },
  { value: "job.cancelled", label: "Job is cancelled" },
  { value: "job.rescheduled", label: "Job is rescheduled" },
  { value: "job.has_unsold_estimate", label: "Job has an unsold estimate" },
  { value: "job.has_sold_estimate", label: "Job has a sold estimate" },
  { value: "estimate.expired", label: "Estimate expires unsigned" },
  { value: "invoice.sent", label: "Invoice is sent" },
  { value: "invoice.paid", label: "Invoice is paid" },
];
const PLATFORM_TRIGGER_EVENT_OPTIONS = [
  { value: "platform.trial_signup", label: "Trial signup" },
  { value: "platform.demo_request", label: "Demo request" },
  { value: "platform.subscription_purchased", label: "Subscription purchased" },
  { value: "platform.affiliate_signup", label: "Affiliate signup" },
];
const TRIGGER_EVENT_LABEL = Object.fromEntries(
  [...TENANT_TRIGGER_EVENT_OPTIONS, ...PLATFORM_TRIGGER_EVENT_OPTIONS].map((o) => [o.value, o.label])
);

function triggerSummary(stage) {
  if (stage.triggerType === "event") {
    return `Trigger: ${TRIGGER_EVENT_LABEL[stage.triggerEvent] || stage.triggerEvent}`;
  }
  if (stage.triggerType === "status") {
    const boardLabel = stage.triggerBoard === "sales" ? "Sales" : "Jobs";
    return `Maps to ${boardLabel} \u2192 ${stage.triggerStatus}`;
  }
  return "No trigger set";
}

export default function Pipeline({ embedded = false }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "super_admin");
  const canSeeAll = user && (user.role === "admin" || user.role === "dispatcher" || user.role === "super_admin");
  const [board, setBoard] = useState(embedded ? "platform-sales" : canSeeAll ? "sales" : "myleads");
  const [pipelines, setPipelines] = useState([]);
  const [stages, setStages] = useState([]);
  const [cards, setCards] = useState([]);
  const [myLeads, setMyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [movingId, setMovingId] = useState(null);
  const [overStage, setOverStage] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newPipelineOpen, setNewPipelineOpen] = useState(false);
  const [pipelineMenuOpen, setPipelineMenuOpen] = useState(false);
  const [editCard, setEditCard] = useState(null); // {card?, stageId}
  const [editTriggerStage, setEditTriggerStage] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [members, setMembers] = useState([]);
  const [openCustomerId, setOpenCustomerId] = useState(null);
  const [openLeadCardId, setOpenLeadCardId] = useState(null);
  const [openAffiliateCard, setOpenAffiliateCard] = useState(null);
  const isPlatformBoard = board === "platform-sales";
  const drag = useRef(null);
  const menuRef = useRef(null);

  const isMyLeads = board === "myleads";
  const isCustom = !isMyLeads && !isSystemBoard(board);
  const activePipeline = pipelines.find((p) => p.id === board);

  // Load custom pipelines list once + customers/users for card modal options.
  useEffect(() => {
    if (canSeeAll) {
      pipelineApi.pipelines().then(setPipelines).catch(() => {});
      customersApi.list().then((cs) => setCustomers(cs.map((c) => ({ id: c.id, name: c.name })))).catch(() => {});
      usersApi.list().then((us) => setMembers(us.map((u) => ({ id: u.id, name: u.name, email: u.email })))).catch(() => {});
    }
  }, [canSeeAll]);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setPipelineMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const load = async (b = board) => {
    setLoading(true);
    try {
      if (b === "myleads") {
        setMyLeads(await pipelineApi.myLeads());
      } else if (b === "jobs") {
        // Jobs pipeline: replaced by the dedicated JobsBoardView (fixed
        // 7-column board with its own filters/data-fetching) — nothing to
        // load into the generic stages/cards state here.
        setStages([]);
        setCards([]);
      } else if (isSystemBoard(b)) {
        const st = await pipelineApi.stages(b);
        setStages(st);
        // Estimates render as before, PLUS one auto-created card per
        // "Sales"-type job (jobId-linked, source: "Job") — merged in so
        // every sales-type job also shows up on this board.
        const [estimates, jobCards] = await Promise.all([estimatesApi.list(), pipelineApi.cards("sales")]);
        setCards([...estimates, ...jobCards]);
      } else {
        const [st, cd] = await Promise.all([pipelineApi.stages(b), pipelineApi.cards(b)]);
        setStages(st);
        setCards(cd);
      }
    } catch (err) {
      toast.error("Failed to load pipeline");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      await load(board);
    })();
  }, [board]);

  // Live auto-update: re-fetch this board the instant a status changes (or any
  // other pipeline-relevant event fires) anywhere in the app — no manual refresh needed.
  useEffect(() => {
    const refresh = () => load(board);
    window.addEventListener("job-status-changed", refresh);
    window.addEventListener("estimate-status-changed", refresh);
    window.addEventListener("pipeline-refresh", refresh);
    return () => {
      window.removeEventListener("job-status-changed", refresh);
      window.removeEventListener("estimate-status-changed", refresh);
      window.removeEventListener("pipeline-refresh", refresh);
    };
  }, [board]);

  const selectBoard = (b) => {
    setPipelineMenuOpen(false);
    setBoard(b);
  };

  // ----- grouping -----
  const cardsByStatus = useMemo(() => {
    const m = {};
    cards.forEach((c) => {
      const key = isCustom ? c.stageId : c.status;
      (m[key] = m[key] || []).push(c);
    });
    return m;
  }, [cards, isCustom]);

  // A system card belongs to the FIRST stage (by order) sharing a status.
  const stageForStatus = useMemo(() => {
    const m = {};
    stages.forEach((s) => {
      if (!(s.status in m)) m[s.status] = s.id;
    });
    return m;
  }, [stages]);

  const cardsForStage = (stage) => {
    if (isCustom) return cardsByStatus[stage.id] || [];
    return (cardsByStatus[stage.status] || []).filter(() => stageForStatus[stage.status] === stage.id);
  };

  const totalForStage = (stage) =>
    cardsForStage(stage).reduce(
      (sum, c) => sum + Number(isCustom ? c.value || 0 : board === "sales" ? c.total || 0 : c.value || 0),
      0
    );

  // ---------- Card move ----------
  const moveCard = async (card, targetStage) => {
    if (isCustom) {
      if (card.stageId === targetStage.id) return;
      setMovingId(card.id);
      try {
        await pipelineApi.updateCard(card.id, { stageId: targetStage.id });
        toast.success(`Moved to ${targetStage.name}`);
        await load(board);
      } catch (err) {
        toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not move card");
      } finally {
        setMovingId(null);
      }
      return;
    }
    if (card.status === targetStage.status) return;
    if (board === "sales" && card.status === "Converted") {
      toast.info("Converted estimates are locked (an invoice already exists).");
      return;
    }
    if (board === "sales" && card.source === "Job") {
      toast.info("This card tracks a job automatically — it moves on its own.");
      return;
    }
    setMovingId(card.id);
    try {
      if (board === "sales") {
        if (targetStage.status === "Converted") {
          await estimatesApi.convertToInvoice(card.id);
          toast.success(`${card.id} won — invoice created`);
        } else {
          await estimatesApi.setStatus(card.id, targetStage.status);
          toast.success(`${card.id} → ${targetStage.name}`);
        }
      } else {
        await jobsApi.updateStatus(card.id, targetStage.status);
        toast.success(`${card.id} → ${targetStage.name}`);
      }
      await load(board);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not move card");
    } finally {
      setMovingId(null);
    }
  };

  // ---------- Column reorder ----------
  const reorderTo = async (targetStageId) => {
    const fromId = drag.current?.stageId;
    if (!fromId || fromId === targetStageId) return;
    const ids = stages.map((s) => s.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(targetStageId);
    ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, fromId);
    const reordered = ids.map((id) => stages.find((s) => s.id === id));
    setStages(reordered);
    try {
      await pipelineApi.reorderStages(board, ids);
    } catch {
      load(board);
    }
  };

  const renameStage = async (stage, name) => {
    if (!name.trim() || name === stage.name) return;
    setStages((prev) => prev.map((s) => (s.id === stage.id ? { ...s, name } : s)));
    try {
      await pipelineApi.updateStage(stage.id, { name: name.trim() });
    } catch (err) {
      toast.error("Rename failed");
      load(board);
    }
  };

  const deleteStage = async (stage) => {
    if (!window.confirm(`Delete the "${stage.name}" column?`)) return;
    try {
      await pipelineApi.deleteStage(stage.id);
      setStages((prev) => prev.filter((s) => s.id !== stage.id));
      toast.success(`Deleted "${stage.name}"`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  // ---------- Pipeline (board) management ----------
  const renamePipeline = async () => {
    const name = window.prompt("Rename pipeline", activePipeline?.name || "");
    if (name == null || !name.trim() || name.trim() === activePipeline?.name) return;
    try {
      const updated = await pipelineApi.updatePipeline(board, { name: name.trim() });
      setPipelines((prev) => prev.map((p) => (p.id === board ? updated : p)));
      toast.success("Pipeline renamed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Rename failed");
    }
    setPipelineMenuOpen(false);
  };

  const deletePipeline = async () => {
    if (!window.confirm(`Delete the "${activePipeline?.name}" pipeline and all its cards? This cannot be undone.`)) return;
    try {
      await pipelineApi.deletePipeline(board);
      setPipelines((prev) => prev.filter((p) => p.id !== board));
      toast.success("Pipeline deleted");
      setBoard("sales");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
    setPipelineMenuOpen(false);
  };

  const usedStatuses = new Set(stages.map((s) => s.status));
  const availableStatuses = board === "sales" ? SALES_STATUSES : JOB_STATUSES;

  const subtitle = isMyLeads
    ? "Leads assigned to you"
    : isCustom
    ? `${activePipeline?.name || "Pipeline"} — cards by stage`
    : board === "sales"
    ? "Sales — estimates by stage"
    : "Jobs — work in progress";

  usePageHeader(
    {
      title: "Pipeline",
      subtitle,
      actions:
        canSeeAll && !isMyLeads && board !== "jobs" ? (
          <Button
            onClick={() => setAddOpen(true)}
            data-testid="pipeline-add-stage-btn"
            className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]"
          >
            <Plus className="h-4 w-4 mr-1.5" /> Add stage
          </Button>
        ) : null,
    },
    !embedded,
  );

  return (
    <>
      <div className="px-6 pt-4">
        {/* Board tabs */}
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 overflow-x-auto no-scrollbar" data-testid="pipeline-board-tabs">
            {!embedded && <BoardTab id="myleads" label="My Leads" Icon={Inbox} active={board === "myleads"} onClick={() => selectBoard("myleads")} />}
            {!embedded && canSeeAll && <BoardTab id="sales" label="Sales" Icon={FileText} active={board === "sales"} onClick={() => selectBoard("sales")} />}
            {!embedded && canSeeAll && <BoardTab id="jobs" label="Jobs" Icon={Briefcase} active={board === "jobs"} onClick={() => selectBoard("jobs")} />}
            {canSeeAll && pipelines.filter((p) => !p.isSystem).map((p) => (
              <BoardTab
                key={p.id}
                id={p.id}
                label={p.name}
                color={p.color}
                active={board === p.id}
                onClick={() => selectBoard(p.id)}
              />
            ))}
          </div>

          {isAdmin && (
            <button
              onClick={() => setNewPipelineOpen(true)}
              data-testid="pipeline-new-board-btn"
              className="inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-dashed border-slate-300 text-slate-500 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/40 text-[13px] font-medium transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> New pipeline
            </button>
          )}

          {isCustom && isAdmin && (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setPipelineMenuOpen((o) => !o)}
                data-testid="pipeline-settings-btn"
                className={
                  embedded
                    ? "h-10 w-10 rounded-lg text-white hover:bg-white/10 flex items-center justify-center"
                    : "h-9 w-9 rounded-lg border border-slate-200 text-slate-500 hover:bg-slate-50 flex items-center justify-center"
                }
              >
                <Settings2 className={embedded ? "h-5 w-5" : "h-4 w-4"} />
              </button>
              {pipelineMenuOpen && (
                <div className="absolute right-0 mt-1.5 w-44 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden z-40" data-testid="pipeline-settings-menu">
                  <button onClick={renamePipeline} data-testid="pipeline-rename-btn" className="w-full px-3 py-2 text-left text-[13px] text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                    <Pencil className="h-3.5 w-3.5" /> Rename pipeline
                  </button>
                  <button onClick={deletePipeline} data-testid="pipeline-delete-btn" className="w-full px-3 py-2 text-left text-[13px] text-rose-600 hover:bg-rose-50 flex items-center gap-2">
                    <Trash2 className="h-3.5 w-3.5" /> Delete pipeline
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-32 text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : isMyLeads ? (
        <MyLeadsView leads={myLeads} onOpenCustomer={setOpenCustomerId} />
      ) : board === "jobs" ? (
        <JobsBoardView />
      ) : (
        <div className="p-6 overflow-x-auto" data-testid="pipeline-board">
          <div className="flex gap-3 items-start min-h-[60vh]" style={{ minWidth: "min-content" }}>
            {stages.map((stage) => {
              const stageCards = cardsForStage(stage);
              const cardsList = (
                <>
                  {stageCards.map((card) =>
                    isCustom ? (
                      card.affiliateId ? (
                        <AffiliateCard
                          key={card.id}
                          card={card}
                          moving={movingId === card.id}
                          onOpen={() => setOpenAffiliateCard(card)}
                          onEdit={() => setEditCard({ card })}
                          onDragStart={() => {
                            drag.current = { kind: "card", card };
                          }}
                          onDragEnd={() => {
                            drag.current = null;
                            setOverStage(null);
                          }}
                        />
                      ) : (
                      <CustomCard
                        key={card.id}
                        card={card}
                        moving={movingId === card.id}
                        onOpen={() => (
                          isPlatformBoard
                            ? setOpenLeadCardId(card.id)
                            : card.jobId
                            ? navigate(`/jobs?id=${card.jobId}`)
                            : card.customerId ? setOpenCustomerId(card.customerId) : setEditCard({ card })
                        )}
                        onEdit={() => setEditCard({ card })}
                        onDragStart={() => {
                          drag.current = { kind: "card", card };
                        }}
                        onDragEnd={() => {
                          drag.current = null;
                          setOverStage(null);
                        }}
                      />
                      )
                    ) : (
                      <KanbanCard
                        key={card.id}
                        card={card}
                        board={board}
                        moving={movingId === card.id}
                        locked={board === "sales" && (card.status === "Converted" || card.source === "Job")}
                        onOpen={() => (
                          board === "jobs"
                            ? navigate(`/jobs?id=${card.id}`)
                            : card.jobId
                            ? navigate(`/jobs?id=${card.jobId}`)
                            : card.customerId ? setOpenCustomerId(card.customerId) : navigate(board === "sales" ? "/estimates" : "/dispatch")
                        )}
                        onDragStart={() => {
                          drag.current = { kind: "card", card };
                        }}
                        onDragEnd={() => {
                          drag.current = null;
                          setOverStage(null);
                        }}
                      />
                    )
                  )}
                  {stageCards.length === 0 && (
                    <div className="text-[11.5px] text-slate-300 text-center py-6 border-2 border-dashed border-slate-200 rounded-lg">
                      Drop here
                    </div>
                  )}
                </>
              );
              const addCardBtn = isCustom && (
                <button
                  onClick={() => setEditCard({ stageId: stage.id })}
                  data-testid={`add-card-${stage.id}`}
                  className="w-full mt-1 py-1.5 rounded-lg text-[12px] font-medium text-slate-400 hover:text-blue-600 hover:bg-blue-50/50 flex items-center justify-center gap-1 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5" /> Add card
                </button>
              );
              const cardDropHandlers = {
                onDragOver: (e) => {
                  if (drag.current?.kind === "card") {
                    e.preventDefault();
                    setOverStage(stage.id);
                  }
                },
                onDragLeave: () => setOverStage((s) => (s === stage.id ? null : s)),
                onDrop: () => {
                  if (drag.current?.kind === "card") {
                    moveCard(drag.current.card, stage);
                    setOverStage(null);
                  }
                },
              };
              return (
                <div
                  key={stage.id}
                  data-testid={`pipeline-column-${stage.id}`}
                  className={`w-[280px] shrink-0 rounded-xl border transition-colors ${
                    overStage === stage.id
                      ? "border-blue-400 bg-blue-50/40"
                      : overColumn === stage.id
                      ? "border-slate-400 bg-slate-50"
                      : "border-slate-200 bg-slate-50/60"
                  }`}
                  onDragOver={(e) => {
                    if (drag.current?.kind === "column") {
                      e.preventDefault();
                      setOverColumn(stage.id);
                    }
                  }}
                  onDrop={() => {
                    if (drag.current?.kind === "column") {
                      reorderTo(stage.id);
                      setOverColumn(null);
                    }
                  }}
                >
                  <ColumnHeader
                    stage={stage}
                    count={stageCards.length}
                    total={totalForStage(stage)}
                    isCustomStage={isCustom}
                    onRename={renameStage}
                    onDelete={deleteStage}
                    onEditTrigger={() => setEditTriggerStage(stage)}
                    onDragStart={() => {
                      drag.current = { kind: "column", stageId: stage.id };
                    }}
                    onDragEnd={() => {
                      drag.current = null;
                      setOverColumn(null);
                    }}
                  />

                  {embedded ? (
                    <>
                      {/* Fixed visible height (~10 cards) — scrolls independently; header above and Add card below never scroll away. */}
                      <div
                        className="px-2 space-y-2 min-h-[80px] overflow-y-auto"
                        style={{ maxHeight: 760 }}
                        data-testid={`pipeline-column-scroll-${stage.id}`}
                        {...cardDropHandlers}
                      >
                        {cardsList}
                      </div>
                      {isCustom && <div className="px-2 pb-2 shrink-0">{addCardBtn}</div>}
                    </>
                  ) : (
                    <div className="px-2 pb-2 space-y-2 min-h-[120px]" {...cardDropHandlers}>
                      {cardsList}
                      {addCardBtn}
                    </div>
                  )}
                </div>
              );
            })}

            <button
              onClick={() => setAddOpen(true)}
              data-testid="pipeline-add-column"
              className="w-[200px] shrink-0 h-[120px] rounded-xl border-2 border-dashed border-slate-200 text-slate-400 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50/30 transition-colors flex flex-col items-center justify-center gap-1.5 text-[13px] font-medium"
            >
              <Plus className="h-5 w-5" /> Add stage
            </button>
          </div>
        </div>
      )}

      {addOpen && (
        <AddStageModal
          board={board}
          isCustom={isCustom}
          availableStatuses={availableStatuses}
          usedStatuses={usedStatuses}
          embedded={embedded}
          onClose={() => setAddOpen(false)}
          onCreated={(stage) => {
            setStages((prev) => [...prev, stage]);
            setAddOpen(false);
            toast.success(`Added "${stage.name}"`);
          }}
        />
      )}

      {newPipelineOpen && (
        <NewPipelineModal
          onClose={() => setNewPipelineOpen(false)}
          onCreated={(pipe) => {
            setPipelines((prev) => [...prev, pipe]);
            setNewPipelineOpen(false);
            setBoard(pipe.id);
            toast.success(`Created "${pipe.name}"`);
          }}
        />
      )}

      {editCard && (
        <CustomCardModal
          pipelineId={board}
          stages={stages}
          defaultStageId={editCard.stageId}
          card={editCard.card}
          customers={customers}
          users={members}
          salesMode={isPlatformBoard}
          onClose={() => setEditCard(null)}
          onSaved={() => {
            setEditCard(null);
            load(board);
          }}
          onDeleted={() => {
            setEditCard(null);
            load(board);
          }}
        />
      )}

      {editTriggerStage && (
        <EditTriggerModal
          stage={editTriggerStage}
          embedded={embedded}
          onClose={() => setEditTriggerStage(null)}
          onSaved={(updated) => {
            setStages((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
            setEditTriggerStage(null);
            toast.success("Trigger updated");
          }}
        />
      )}

      {openCustomerId && (
        <CustomerProfileOverlay customerId={openCustomerId} onClose={() => setOpenCustomerId(null)} />
      )}
      {openLeadCardId && (
        <PlatformLeadDrawer
          cardId={openLeadCardId}
          stages={stages}
          onClose={() => setOpenLeadCardId(null)}
          onCardUpdated={() => load()}
          onCardDeleted={() => { setOpenLeadCardId(null); load(); }}
        />
      )}
      {openAffiliateCard && (
        <AffiliateCardDetailModal card={openAffiliateCard} onClose={() => setOpenAffiliateCard(null)} />
      )}
    </>
  );
}

function MyLeadsView({ leads, onOpenCustomer }) {
  const groups = useMemo(() => {
    const m = {};
    (leads || []).forEach((l) => {
      const key = l.pipelineName || "Pipeline";
      (m[key] = m[key] || []).push(l);
    });
    return m;
  }, [leads]);

  if (!leads || leads.length === 0) {
    return (
      <div className="p-6" data-testid="my-leads-empty">
        <div className="max-w-md mx-auto text-center py-20 text-slate-400">
          <Inbox className="h-10 w-10 mx-auto mb-3 text-slate-300" />
          <div className="text-[15px] font-semibold text-slate-600">No leads assigned to you yet</div>
          <div className="text-[13px] mt-1">When a manager assigns you a lead, it shows up here.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="my-leads-view">
      {Object.entries(groups).map(([pipeName, items]) => (
        <div key={pipeName}>
          <div className="text-[11px] uppercase tracking-wider font-bold text-slate-400 mb-2">{pipeName} · {items.length}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {items.map((card) => {
              const overdue = card.dueAt && new Date(card.dueAt) < new Date(new Date().toDateString());
              return (
                <div
                  key={card.id}
                  data-testid={`my-lead-${card.id}`}
                  className="rounded-xl border border-slate-200 bg-white p-3.5 shadow-sm hover:shadow-md hover:border-blue-300 transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-[13.5px] font-bold text-slate-900 truncate">{card.title}</div>
                      {card.customerName ? (
                        <button
                          onClick={() => card.customerId && onOpenCustomer(card.customerId)}
                          disabled={!card.customerId}
                          data-testid={`my-lead-customer-${card.id}`}
                          className="text-[12px] text-blue-600 hover:underline truncate disabled:text-slate-400 disabled:no-underline"
                        >
                          {card.customerName}
                        </button>
                      ) : null}
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{card.stageName}</span>
                  </div>
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    {Number(card.value) > 0 && (
                      <span className="inline-flex items-center gap-0.5 text-[12px] font-bold text-emerald-600">
                        <DollarSign className="h-3 w-3" /> {money(card.value)}
                      </span>
                    )}
                    {(card.tags || []).slice(0, 2).map((t) => (
                      <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-700">
                        <TagIcon className="h-2.5 w-2.5" /> {t}
                      </span>
                    ))}
                    {card.dueAt && (
                      <span className={`inline-flex items-center gap-1 text-[10.5px] ${overdue ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
                        <Calendar className="h-3 w-3" /> {fmtDate(card.dueAt)}
                      </span>
                    )}
                    {card.source && card.source !== "Manual" && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{card.source}</span>
                    )}
                  </div>
                  {card.customerId && (
                    <button
                      onClick={() => onOpenCustomer(card.customerId)}
                      data-testid={`my-lead-open-${card.id}`}
                      className="mt-3 w-full h-8 rounded-lg border border-slate-200 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 hover:border-blue-300 hover:text-blue-700 transition-colors"
                    >
                      Open customer file
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function BoardTab({ id, label, Icon, color, active, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`pipeline-tab-${id}`}
      className={`inline-flex items-center gap-1.5 h-8 px-4 rounded-md text-[13px] font-semibold whitespace-nowrap transition-colors ${
        active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
      }`}
    >
      {Icon ? (
        <Icon className="h-3.5 w-3.5" />
      ) : (
        <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color || "#94A3B8" }} />
      )}
      {label}
    </button>
  );
}

function ColumnHeader({ stage, count, total, isCustomStage, onRename, onDelete, onEditTrigger, onDragStart, onDragEnd }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(stage.name);

  const commit = () => {
    setEditing(false);
    onRename(stage, name);
  };

  return (
    <div className="px-2.5 py-2.5">
      <div
        draggable={!editing}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing group"
      >
        <GripVertical className="h-3.5 w-3.5 text-slate-300 group-hover:text-slate-400 shrink-0" />
        {editing ? (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") {
                setName(stage.name);
                setEditing(false);
              }
            }}
            data-testid={`stage-rename-input-${stage.id}`}
            className="flex-1 h-6 px-1.5 rounded border border-blue-300 text-[13px] font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        ) : (
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            <span className="text-[13px] font-bold text-slate-800 truncate" data-testid={`stage-name-${stage.id}`}>
              {stage.name}
            </span>
            <span className="text-[11px] font-semibold text-slate-400 shrink-0">{count}</span>
          </div>
        )}
        {!editing && (
          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {isCustomStage && (
              <button
                onClick={onEditTrigger}
                data-testid={`stage-trigger-edit-${stage.id}`}
                title="Configure trigger"
                className="h-6 w-6 rounded hover:bg-blue-50 flex items-center justify-center text-slate-400 hover:text-blue-600"
              >
                <Settings2 className="h-3 w-3" />
              </button>
            )}
            <button
              onClick={() => { setName(stage.name); setEditing(true); }}
              data-testid={`stage-edit-${stage.id}`}
              className="h-6 w-6 rounded hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              onClick={() => onDelete(stage)}
              data-testid={`stage-delete-${stage.id}`}
              className="h-6 w-6 rounded hover:bg-rose-50 flex items-center justify-center text-slate-400 hover:text-rose-600"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        )}
        {editing && (
          <button onClick={commit} className="h-6 w-6 rounded hover:bg-emerald-50 flex items-center justify-center text-emerald-600">
            <Check className="h-3.5 w-3.5" />
          </button>
        )}
        {total > 0 && !editing && (
          <span className="text-[10.5px] font-bold text-emerald-600 shrink-0 ml-1">{money(total)}</span>
        )}
      </div>
      {isCustomStage && !editing && (
        <button
          onClick={onEditTrigger}
          data-testid={`stage-trigger-summary-${stage.id}`}
          className="mt-1 ml-5 text-[10.5px] text-slate-400 hover:text-blue-600 truncate block max-w-full"
        >
          {triggerSummary(stage)}
        </button>
      )}
    </div>
  );
}

function CustomCard({ card, moving, onOpen, onEdit, onDragStart, onDragEnd }) {
  const overdue = card.dueAt && new Date(card.dueAt) < new Date(new Date().toDateString());
  return (
    <div
      draggable={!moving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`pipeline-card-${card.id}`}
      className={`group/card relative rounded-lg border bg-white p-2.5 shadow-sm transition-all border-slate-200 hover:border-blue-300 hover:shadow-md cursor-pointer ${
        moving ? "opacity-50" : ""
      }`}
    >
      {onEdit && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`pipeline-card-edit-${card.id}`}
          title="Edit / assign"
          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md bg-white/90 border border-slate-200 text-slate-400 hover:text-blue-600 hover:border-blue-300 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity"
        >
          <PencilIcon className="h-3 w-3" />
        </button>
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[12.5px] font-bold text-slate-900 truncate">{card.title}</div>
          {card.customerName && (
            <div className="text-[11px] text-blue-600 truncate hover:underline">{card.customerName}</div>
          )}
        </div>
        {Number(card.value) > 0 && (
          <span className="inline-flex items-center gap-0.5 text-[12px] font-bold text-emerald-600 shrink-0 mr-7">
            <DollarSign className="h-3 w-3" />
            {money(card.value)}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-1.5 flex-wrap">
        {(card.tags || []).slice(0, 3).map((t) => (
          <span key={t} className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded border border-violet-200 bg-violet-50 text-violet-700">
            <TagIcon className="h-2.5 w-2.5" /> {t}
          </span>
        ))}
        {card.assigneeName && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500">
            <User className="h-3 w-3" /> {card.assigneeName}
          </span>
        )}
        {card.dueAt && (
          <span className={`inline-flex items-center gap-1 text-[10.5px] ${overdue ? "text-rose-600 font-semibold" : "text-slate-500"}`}>
            <Calendar className="h-3 w-3" /> {fmtDate(card.dueAt)}
          </span>
        )}
        {card.source && card.source !== "Manual" && (
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{card.source}</span>
        )}
      </div>
    </div>
  );
}

function AffiliateCard({ card, moving, onOpen, onEdit, onDragStart, onDragEnd }) {
  const isActive = (card.affiliateStatus || "active") === "active";
  return (
    <div
      draggable={!moving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`pipeline-card-${card.id}`}
      className={`group/card relative rounded-lg border bg-white p-2.5 shadow-sm transition-all border-slate-200 hover:border-amber-300 hover:shadow-md cursor-pointer ${
        moving ? "opacity-50" : ""
      }`}
    >
      {onEdit && (
        <button
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
          data-testid={`pipeline-card-edit-${card.id}`}
          title="Edit / assign"
          className="absolute top-1.5 right-1.5 h-6 w-6 rounded-md bg-white/90 border border-slate-200 text-slate-400 hover:text-amber-600 hover:border-amber-300 flex items-center justify-center opacity-0 group-hover/card:opacity-100 transition-opacity"
        >
          <PencilIcon className="h-3 w-3" />
        </button>
      )}
      <div className="flex items-start justify-between gap-2 pr-6">
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-bold text-slate-900 truncate">{card.affiliateName || card.title}</div>
          {card.affiliateEmail && (
            <div className="flex items-center gap-1 text-[11px] text-slate-500 truncate mt-0.5">
              <Mail className="h-3 w-3 shrink-0" />
              <span className="truncate">{card.affiliateEmail}</span>
            </div>
          )}
          {card.affiliatePhone && (
            <div className="flex items-center gap-1 text-[11px] text-slate-500 truncate mt-0.5">
              <Phone className="h-3 w-3 shrink-0" />
              <span className="truncate">{card.affiliatePhone}</span>
            </div>
          )}
        </div>
        <span
          data-testid={`pipeline-card-status-${card.id}`}
          className={`inline-flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full shrink-0 whitespace-nowrap ${
            isActive ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"
          }`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
          {isActive ? "Active" : "Inactive"}
        </span>
      </div>
      {card.affiliateCouponCode && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700">
          <Ticket className="h-3 w-3" /> {card.affiliateCouponCode}
        </div>
      )}
    </div>
  );
}

function AffiliateDetailRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="h-7 w-7 rounded-lg bg-slate-50 border border-slate-100 flex items-center justify-center shrink-0">
        <Icon className="h-3.5 w-3.5 text-slate-400" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
        <div className="text-[13px] text-slate-800 truncate">{value}</div>
      </div>
    </div>
  );
}

function AffiliateRateStat({ label, value }) {
  return (
    <div className="rounded-lg bg-amber-50 border border-amber-100 px-2 py-2 text-center">
      <div className="text-[13px] font-bold text-amber-700">{value}</div>
      <div className="text-[9.5px] text-amber-600/80 mt-0.5">{label}</div>
    </div>
  );
}

function AffiliateCardDetailModal({ card, onClose }) {
  const isActive = (card.affiliateStatus || "active") === "active";
  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="affiliate-card-detail-modal"
        className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden"
      >
        <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between">
          <div>
            <div className="text-base font-bold text-slate-900">{card.affiliateName || card.title}</div>
            <span className={`inline-flex items-center gap-1 mt-1.5 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${
              isActive ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-slate-100 text-slate-500 border border-slate-200"
            }`}>
              <span className={`h-1.5 w-1.5 rounded-full ${isActive ? "bg-emerald-500" : "bg-slate-400"}`} />
              {isActive ? "Active" : "Inactive"}
            </span>
          </div>
          <button
            onClick={onClose}
            data-testid="affiliate-card-detail-close"
            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-3">
          <AffiliateDetailRow icon={Mail} label="Email" value={card.affiliateEmail || "—"} />
          <AffiliateDetailRow icon={Phone} label="Phone" value={card.affiliatePhone || "—"} />
          <AffiliateDetailRow icon={Ticket} label="Coupon code" value={card.affiliateCouponCode || "—"} />
          <div className="grid grid-cols-3 gap-2 pt-1">
            <AffiliateRateStat label="1st purchase" value={`${card.affiliateFirstPurchasePct ?? 0}%`} />
            <AffiliateRateStat label="Credits" value={`${card.affiliateCreditsPct ?? 0}%`} />
            <AffiliateRateStat label="Discount" value={`${card.affiliateDiscountPct ?? 0}%`} />
          </div>
          <AffiliateDetailRow icon={Users} label="Referrals" value={String(card.affiliateReferralCount ?? 0)} />
          <AffiliateDetailRow
            icon={Calendar}
            label="Approved"
            value={fmtDate(card.affiliateApprovedAt || card.createdAt) || "—"}
          />
        </div>
      </div>
    </div>
  );
}

function KanbanCard({ card, board, moving, locked, onOpen, onDragStart, onDragEnd }) {
  const isSales = board === "sales";
  const isJobCard = !isSales || card.source === "Job";
  const navigate = useNavigate();
  return (
    <div
      draggable={!locked && !moving}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`pipeline-card-${card.id}`}
      className={`rounded-lg border bg-white p-2.5 shadow-sm transition-all ${
        locked ? "border-slate-200 opacity-90 cursor-default" : "border-slate-200 hover:border-blue-300 hover:shadow-md cursor-grab active:cursor-grabbing"
      } ${moving ? "opacity-50" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {isJobCard ? (
            <>
              <div className="text-[12.5px] font-bold text-slate-900 truncate">{card.title || card.type || "Job"}</div>
              <div className="text-[11px] text-slate-600 truncate">{card.customer}</div>
              <div className="text-[10.5px] text-slate-500 truncate flex items-center gap-1">
                <User className="h-2.5 w-2.5" /> {card.tech || "Unassigned"}
              </div>
              <div className="text-[10px] text-slate-400 truncate">{card.id}</div>
            </>
          ) : (
            <>
              <div className="text-[12.5px] font-bold text-slate-900 truncate">{card.customer}</div>
              <div className="text-[11px] text-slate-500 truncate">
                {card.id} · {card.title || "Estimate"}
              </div>
            </>
          )}
        </div>
        {locked ? (
          <Lock className="h-3.5 w-3.5 text-slate-300 shrink-0 mt-0.5" />
        ) : (
          <span className="inline-flex items-center gap-0.5 text-[12px] font-bold text-emerald-600 shrink-0">
            <DollarSign className="h-3 w-3" />
            {money(isSales ? card.total : card.value)}
          </span>
        )}
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap">
        {!isSales && card.priority && (
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${PRIORITY_COLOR[card.priority] || PRIORITY_COLOR.Low}`}>
            {card.priority}
          </span>
        )}
        {!isSales && card.start && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-slate-500">
            <Calendar className="h-3 w-3" /> {fmtDate(card.start)}
          </span>
        )}
        {!isSales && card.estimateId && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/estimates?id=${card.estimateId}`);
            }}
            data-testid={`pipeline-card-estlink-${card.id}`}
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <FileText className="h-3 w-3" /> from {card.estimateId}
          </button>
        )}
        {isSales && !isJobCard && card.signature && (
          <span className="inline-flex items-center gap-1 text-[10.5px] text-emerald-600 font-medium">
            <Check className="h-3 w-3" /> Signed
          </span>
        )}
        {isSales && !isJobCard && card.depositPaid > 0 && (
          <span className="text-[10px] font-semibold text-emerald-600">Deposit {money(card.depositPaid)}</span>
        )}
      </div>
    </div>
  );
}

function AddStageModal({ board, isCustom, availableStatuses, usedStatuses, embedded, onClose, onCreated }) {
  const eventOptions = embedded ? PLATFORM_TRIGGER_EVENT_OPTIONS : TENANT_TRIGGER_EVENT_OPTIONS;
  const [name, setName] = useState("");
  const firstUnused = availableStatuses.find((s) => !usedStatuses.has(s)) || availableStatuses[0];
  const [status, setStatus] = useState(firstUnused);
  const [trigMode, setTrigMode] = useState("event");
  const [trigEvent, setTrigEvent] = useState(eventOptions[0].value);
  const [trigBoard, setTrigBoard] = useState("sales");
  const [trigStatus, setTrigStatus] = useState(SALES_STATUSES[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setError("Stage name is required");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const payload = isCustom
        ? {
            board,
            name: name.trim(),
            triggerType: trigMode,
            ...(trigMode === "event"
              ? { triggerEvent: trigEvent }
              : { triggerBoard: trigBoard, triggerStatus: trigStatus }),
          }
        : { board, name: name.trim(), status };
      const stage = await pipelineApi.createStage(payload);
      onCreated(stage);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not create stage");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="add-stage-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Add a stage</div>
          <button onClick={onClose} data-testid="add-stage-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3.5" data-testid="add-stage-form">
          <div>
            <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">Column name</label>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="add-stage-name"
              placeholder="e.g. Awaiting deposit"
              className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
            />
          </div>
          {!isCustom && (
            <div>
              <label className="block text-[12px] font-semibold text-slate-700 mb-1.5">
                Maps to status <span className="text-slate-400 font-normal">— dragging a card here sets this status</span>
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="add-stage-status"
                className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
              >
                {availableStatuses.map((s) => (
                  <option key={s} value={s}>
                    {s}
                    {usedStatuses.has(s) ? " (already used)" : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          {isCustom && (
            <TriggerFields
              embedded={embedded}
              mode={trigMode}
              setMode={setTrigMode}
              event={trigEvent}
              setEvent={setTrigEvent}
              triggerBoard={trigBoard}
              setTriggerBoard={setTrigBoard}
              status={trigStatus}
              setStatus={setTrigStatus}
              prefix="add-stage-trigger"
            />
          )}
          {error && (
            <div data-testid="add-stage-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="add-stage-submit" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
              {busy ? "Adding…" : "Add stage"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TriggerFields({ mode, setMode, event, setEvent, triggerBoard, setTriggerBoard, status, setStatus, prefix, embedded }) {
  const eventOptions = embedded ? PLATFORM_TRIGGER_EVENT_OPTIONS : TENANT_TRIGGER_EVENT_OPTIONS;
  const statusList = triggerBoard === "sales" ? SALES_STATUSES : JOB_STATUSES;
  return (
    <div className="space-y-2">
      <label className="block text-[12px] font-semibold text-slate-700">
        Trigger <span className="text-rose-500">*</span>
        <span className="text-slate-400 font-normal block mt-0.5">When should a card automatically land in this column?</span>
      </label>
      {!embedded && (
        <div className="inline-flex rounded-lg border border-slate-200 p-0.5 bg-slate-50">
          <button
            type="button"
            onClick={() => setMode("event")}
            data-testid={`${prefix}-mode-event`}
            className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
              mode === "event" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"
            }`}
          >
            System event
          </button>
          <button
            type="button"
            onClick={() => setMode("status")}
            data-testid={`${prefix}-mode-status`}
            className={`h-8 px-3 rounded-md text-[12.5px] font-semibold transition-colors ${
              mode === "status" ? "bg-white shadow-sm text-blue-700" : "text-slate-500"
            }`}
          >
            Map to existing status
          </button>
        </div>
      )}
      {(embedded || mode === "event") ? (
        <select
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          data-testid={`${prefix}-event`}
          className="w-full h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
        >
          {eventOptions.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <select
            value={triggerBoard}
            onChange={(e) => {
              const b = e.target.value;
              setTriggerBoard(b);
              setStatus(b === "sales" ? SALES_STATUSES[0] : JOB_STATUSES[0]);
            }}
            data-testid={`${prefix}-board`}
            className="h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            <option value="sales">Sales (Estimate)</option>
            <option value="jobs">Jobs</option>
          </select>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            data-testid={`${prefix}-status`}
            className="h-10 px-3 rounded-lg border border-slate-200 text-[13.5px] bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
          >
            {statusList.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

function EditTriggerModal({ stage, embedded, onClose, onSaved }) {
  const eventOptions = embedded ? PLATFORM_TRIGGER_EVENT_OPTIONS : TENANT_TRIGGER_EVENT_OPTIONS;
  const [trigMode, setTrigMode] = useState(embedded ? "event" : stage.triggerType || "event");
  const [trigEvent, setTrigEvent] = useState(stage.triggerEvent || eventOptions[0].value);
  const [trigBoard, setTrigBoard] = useState(stage.triggerBoard || "sales");
  const [trigStatus, setTrigStatus] = useState(stage.triggerStatus || SALES_STATUSES[0]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const patch = {
        triggerType: trigMode,
        ...(trigMode === "event"
          ? { triggerEvent: trigEvent }
          : { triggerBoard: trigBoard, triggerStatus: trigStatus }),
      };
      const updated = await pipelineApi.updateStage(stage.id, patch);
      onSaved(updated);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Could not update trigger");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md border border-slate-200" data-testid="edit-trigger-modal">
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-slate-900">Trigger for &quot;{stage.name}&quot;</div>
          <button onClick={onClose} data-testid="edit-trigger-close" className="h-7 w-7 rounded hover:bg-slate-100 flex items-center justify-center">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3.5" data-testid="edit-trigger-form">
          <TriggerFields
            embedded={embedded}
            mode={trigMode}
            setMode={setTrigMode}
            event={trigEvent}
            setEvent={setTrigEvent}
            triggerBoard={trigBoard}
            setTriggerBoard={setTrigBoard}
            status={trigStatus}
            setStatus={setTrigStatus}
            prefix="edit-trigger"
          />
          {error && (
            <div data-testid="edit-trigger-error" className="px-3 py-2 rounded-md bg-rose-50 border border-rose-200 text-[12.5px] text-rose-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {error}
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="h-9 text-[13px]">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="edit-trigger-submit" className="h-9 bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-[13px]">
              {busy ? "Saving…" : "Save trigger"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
